import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Card from '../components/Card';
import Table from '../components/Table';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import api from '@/api';
import toast from 'react-hot-toast';
import { toArray } from "../utils/toArray";
import { HiOutlineTrash, HiOutlinePencilSquare, HiOutlineArrowsRightLeft, HiOutlineDocumentArrowDown } from "react-icons/hi2";

const ITEMS_PER_PAGE = 10;

const CandidatsPage = () => {
  const [candidats, setCandidats] = useState([]);
  const [totalCandidats, setTotalCandidats] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchCandidats = useCallback(async (page, search = '') => {
    setIsLoading(true);
    const skip = (page - 1) * ITEMS_PER_PAGE;
    try {
      const response = await api.get(`/candidats/?skip=${skip}&limit=${ITEMS_PER_PAGE}&search=${encodeURIComponent(search)}`);
      setCandidats(Array.isArray(response.data.items) ? response.data.items : []);
      setTotalCandidats(response.data.total || 0);
    } catch (error) {
      console.error("Erreur lors de la récupération des candidats:", error);
      toast.error("Impossible de charger les candidats.");
      setCandidats([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCandidats(currentPage, searchTerm);
  }, [fetchCandidats, currentPage, searchTerm]);

  const handleDelete = async () => {
    const deleteToast = toast.loading('Suppression en cours...');
    try {
      await api.delete('/candidats/', { data: { ids: selectedIds } });
      setCandidats(prevCandidats => 
        prevCandidats.filter(candidat => !selectedIds.includes(candidat.id))
      );
      toast.success(`${selectedIds.length} candidat(s) supprimé(s).`, { id: deleteToast });
      setSelectedIds([]);
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error('Une erreur est survenue.', { id: deleteToast });
    } finally {
      setIsModalOpen(false);
    }
  };

  const candidatsForTable = toArray(candidats).map(candidat => ({
    id: candidat.id,
    sourcer: candidat.sourcer,
    profil: candidat.profil,
    epem: candidat.epem,
    mobilite: candidat.mobilite,
    numeroBullhorn: candidat.numeroBullhorn,
    prenom: candidat.prenom,
    nom: candidat.nom,
    priseDeRef: candidat.priseDeRef,
    formatPixie: candidat.formatPixie,
    competences: candidat.competences,
    xp: candidat.xp,
    dispo: candidat.dispo,
    salaireActuel: candidat.salaireActuel,
    statut: candidat.statut,
    ps: candidat.ps,
    situationProcess: candidat.situationProcess,
    titreDeSejour: candidat.titreDeSejour,
    anglais: candidat.anglais,
    posteActuel: candidat.posteActuel,
    pistePixie: candidat.pistePixie,
    cv: (
        <div className="flex justify-center">
            {candidat.cv_filename ? (
                <a href={`/uploads/${candidat.cv_filename}`} target="_blank" rel="noopener noreferrer" className="text-pro-dark-text-secondary hover:text-pro-dark-accent" title={candidat.cv_filename}>
                    <HiOutlineDocumentArrowDown size={18} />
                </a>
            ) : ( <span className="text-gray-600">-</span> )}
        </div>
    ),
    matching: (
        <div className="flex justify-center">
            <Link to={`/candidats/${candidat.id}/matching`} className="text-pro-dark-text-secondary hover:text-pro-dark-accent" title="Voir les offres correspondantes">
                <HiOutlineArrowsRightLeft size={18} />
            </Link>
        </div>
    ),
    actions: (
        <div className="flex justify-center">
            <Link to={`/candidats/modifier/${candidat.id}`} className="text-pro-dark-text-secondary hover:text-pro-dark-accent" title="Modifier">
                <HiOutlinePencilSquare size={18} />
            </Link>
        </div>
    )
  }));
    
  const columns = [
    { Header: 'Matching', accessor: 'matching' },
    { Header: 'Actions', accessor: 'actions' },
    { Header: 'CV', accessor: 'cv' },
    { Header: 'Sourcer', accessor: 'sourcer' },
    { Header: 'Profil', accessor: 'profil' },
    { Header: 'EP/EM', accessor: 'epem' },
    { Header: 'Mobilité', accessor: 'mobilite' },
    { Header: 'N° Bullhorn', accessor: 'numeroBullhorn' },
    { Header: 'Prénom', accessor: 'prenom' },
    { Header: 'Nom', accessor: 'nom' },
    { Header: 'Prise de Réf', accessor: 'priseDeRef' },
    { Header: 'Format Pixie', accessor: 'formatPixie' },
    { Header: 'Compétences', accessor: 'competences' },
    { Header: 'XP', accessor: 'xp' },
    { Header: 'Dispo', accessor: 'dispo' },
    { Header: 'Salaire actuel', accessor: 'salaireActuel' },
    { Header: 'Statut', accessor: 'statut' },
    { Header: 'PS', accessor: 'ps' },
    { Header: 'Situation process', accessor: 'situationProcess' },
    { Header: 'Titre de séjour', accessor: 'titreDeSejour' },
    { Header: 'Anglais', accessor: 'anglais' },
    { Header: 'Poste Actuel', accessor: 'posteActuel' },
    { Header: 'Piste Pixie', accessor: 'pistePixie' },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold text-pro-dark-text-primary">Candidats</h1>
        <Link to="/candidats/ajouter" className="px-4 py-2 bg-pro-dark-accent text-white rounded-lg hover:bg-pro-dark-accent-hover transition-colors">
          Ajouter un Candidat
        </Link>
      </div>
      <div className="mb-4 h-12 flex items-center">
        {selectedIds.length > 0 ? (
          <div className="flex items-center gap-4 animate-fade-in">
            <span className="text-sm text-pro-dark-text-secondary">{selectedIds.length} sélectionné(s)</span>
            <button 
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all"
            >
              <HiOutlineTrash />
              Supprimer
            </button>
          </div>
        ) : (
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Rechercher..."
            className="w-full max-w-sm p-2 bg-pro-dark-surface border border-pro-dark-border rounded-md focus:ring-pro-dark-accent focus:border-pro-dark-accent"
          />
        )}
      </div>
      <Card>
        {isLoading ? (
          <p className="text-center text-pro-dark-text-secondary">Chargement des données...</p>
        ) : (
          <Table
            columns={columns}
            data={candidatsForTable}
            onSelectionChange={setSelectedIds}
          />
        )}
      </Card>
      <Pagination
        currentPage={currentPage}
        totalItems={totalCandidats}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={(page) => setCurrentPage(page)}
      />
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleDelete}
        title="Confirmer la suppression"
      >
        <p>Êtes-vous sûr de vouloir supprimer définitivement le(s) {selectedIds.length} candidat(s) sélectionné(s) ?</p>
        <p className="mt-2 text-yellow-400">Cette action est irréversible.</p>
      </Modal>
    </div>
  );
};

export default CandidatsPage;